// SceneTypography — chapter copy as part of the composition, never a card.
// Each chapter gets its own placement and phrase. Re-mounting on scene changes
// lets the words arrive with the shot instead of sitting in the UI chrome.

import type { CSSProperties } from 'react';
import type { SceneConfig } from './planetSections';

interface ScenePhrase {
  words: string[];
  caption: string;
  coordinate: string;
}

const PHRASES: Partial<Record<string, ScenePhrase>> = {
  about: {
    words: ['SMALL TOOLS', 'SLOW IDEAS'],
    caption: 'A personal corner of the internet, assembled one careful system at a time.',
    coordinate: 'YH / ORIGIN 01',
  },
  skills: {
    words: ['CODE HAS', 'A MATERIAL'],
    caption: 'Type, motion, models, and the small choices that make a thing feel alive.',
    coordinate: 'YH / MATERIAL 02',
  },
  projects: {
    words: ['THINGS I', 'SENT OUT'],
    caption: 'Projects with their own gravity, released into the world and kept in motion.',
    coordinate: 'YH / ORBIT 03',
  },
  notes: {
    words: ['WORK LEFT', 'OPEN'],
    caption: 'Notes from the middle of making: decisions, reversals, and details worth keeping.',
    coordinate: 'YH / FIELD 04',
  },
  timeline: {
    words: ['NO STRAIGHT', 'LINES HERE'],
    caption: 'A path made from experiments, detours, and the things that survived them.',
    coordinate: 'YH / TRACE 05',
  },
  contact: {
    words: ['SEND A', 'SIGNAL'],
    caption: 'The line is open for collaborations, strange ideas, and useful work.',
    coordinate: 'YH / UPLINK 06',
  },
};

export interface SceneTypographyProps {
  scene: SceneConfig;
  visible: boolean;
}

export function SceneTypography({ scene, visible }: SceneTypographyProps) {
  const phrase = PHRASES[scene.key];
  if (!phrase || !visible) return null;

  return (
    <section
      key={scene.key}
      className={`scene-typography scene-typography--${scene.key}`}
      aria-hidden="true"
    >
      <span className="scene-typography__coordinate">{phrase.coordinate}</span>
      <div className="scene-typography__words">
        {phrase.words.map((word, index) => (
          <span
            key={word}
            className="scene-typography__word"
            style={{ '--word-order': index } as CSSProperties}
          >
            {word}
          </span>
        ))}
      </div>
      <p className="scene-typography__caption">{phrase.caption}</p>
      <span className="scene-typography__scene-number">
        {String(scene.id).padStart(2, '0')} / 06
      </span>
    </section>
  );
}

export default SceneTypography;
